// Anonymous visit counts for our own Umami server (Jake, 2026-11-03; deploy/README.md
// § Analytics). Our own code, not Umami's tracker, so we control exactly what is sent: the
// page path, the referring site's address and the page title, and for an event its name
// and a few fixed values. Nothing is read from the device (no screen size or language), and
// nothing from a rota is ever passed here, and the import worker can't make requests at all (public/_headers).
//
// Off unless the build sets UMAMI_WEBSITE_ID (config/analytics.ts), and off for visitors
// whose browser asks not to be tracked.

/** Same origin; nginx forwards it to Umami's /api/send. */
export const STATS_ENDPOINT = '/api/stats';
export const STATS_META = 'shiftlens-stats';

/** Every event we send, with its only allowed values. */
export interface StatEvents {
  'Rota checked': { source: 'file' | 'sample' };
}

interface Payload {
  website: string;
  hostname: string;
  url: string;
  referrer: string;
  title: string;
  name?: string;
  data?: Record<string, string>;
}

function websiteId(): string | undefined {
  return (
    document.querySelector<HTMLMetaElement>(`meta[name="${STATS_META}"]`)?.content || undefined
  );
}

function optedOut(): boolean {
  const nav = navigator as Navigator & { globalPrivacyControl?: boolean };
  return nav.doNotTrack === '1' || nav.globalPrivacyControl === true;
}

/** The referring site, without its path or query: enough to see search engines and links. */
export function referrerOrigin(referrer: string, ownOrigin: string): string {
  try {
    const origin = new URL(referrer).origin;
    return origin === ownOrigin || origin === 'null' ? '' : `${origin}/`;
  } catch {
    return '';
  }
}

export function buildPayload<E extends keyof StatEvents>(
  website: string,
  event?: { name: E; data: StatEvents[E] },
): Payload {
  return {
    website,
    hostname: location.hostname,
    url: location.pathname,
    referrer: referrerOrigin(document.referrer, location.origin),
    title: document.title,
    ...(event && { name: event.name, data: { ...event.data } }),
  };
}

function send(payload: Payload): void {
  void fetch(STATS_ENDPOINT, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ type: 'event', payload }),
    credentials: 'omit',
    keepalive: true,
  }).catch(() => {
    // Counting is best effort. A blocked or failed request changes nothing for the visitor.
  });
}

export function trackPageview(): void {
  const website = websiteId();
  if (website && !optedOut()) send(buildPayload(website));
}

export function trackEvent<E extends keyof StatEvents>(name: E, data: StatEvents[E]): void {
  const website = websiteId();
  if (website && !optedOut()) send(buildPayload(website, { name, data }));
}
